import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { VehicleApiService } from '@raptor-fe/services';
import { VehicleSpecCategory, VehicleSpecification } from '@raptor-fe/core';

export interface VehicleSpecModalState {
    tenantId: string;
    branchId: string;
    vehicleId: string;
    categories: VehicleSpecCategory[];
    selectedCategoryId: number | null;
    specifications: VehicleSpecification[];
    loadingCategories: boolean;
    loadingSpecs: boolean;
}

/**
 * Drives the read-only vehicle-specifications viewer (a synchronous lookup, no punchout/callback): load the
 * specification categories by VIN, then the specifications within a chosen category. The component owns only the
 * dialog lifecycle and value formatting -- all data loading lives here.
 */
export const VehicleSpecModalStore = signalStore(
    withState<VehicleSpecModalState>({
        tenantId: '',
        branchId: '',
        vehicleId: '',
        categories: [],
        selectedCategoryId: null,
        specifications: [],
        loadingCategories: false,
        loadingSpecs: false,
    }),
    withMethods((store, service = inject(VehicleApiService)) => {
        const fetchCategories = rxMethod<void>(
            pipe(
                tap(() => patchState(store, { loadingCategories: true })),
                switchMap(() =>
                    service.getSpecificationCategories(store.tenantId(), store.vehicleId()).pipe(
                        tapResponse({
                            next: (categories) => patchState(store, { categories: categories ?? [] }),
                            error: () => patchState(store, { categories: [] }),
                            finalize: () => patchState(store, { loadingCategories: false }),
                        }),
                    ),
                ),
            ),
        );

        const loadSpecifications = rxMethod<number>(
            pipe(
                tap(() => patchState(store, { loadingSpecs: true })),
                switchMap((categoryId) =>
                    service.getSpecifications(store.tenantId(), store.vehicleId(), categoryId).pipe(
                        tapResponse({
                            next: (specifications) => patchState(store, { specifications: specifications ?? [] }),
                            error: () => patchState(store, { specifications: [] }),
                            finalize: () => patchState(store, { loadingSpecs: false }),
                        }),
                    ),
                ),
            ),
        );

        return {
            fetchCategories,
            loadSpecifications,

            /** Seeds the vehicle context and loads the specification categories. */
            init(tenantId: string, branchId: string, vehicleId: string): void {
                patchState(store, { tenantId, branchId, vehicleId });
                fetchCategories();
            },

            /** Selects a category and loads the specifications within it. */
            pickCategory(categoryId: number): void {
                patchState(store, { selectedCategoryId: categoryId });
                loadSpecifications(categoryId);
            },
        };
    }),
);
